/* eslint-disable @typescript-eslint/no-explicit-any */
import React, { useMemo } from "react";
import { IonContent, IonPage } from "@ionic/react";
import { useParams, withRouter } from "react-router";
import ContentContainer from "@components/common/ContentContainer";
import DataController from "@controllers/DataController";
import { EventRegister } from "react-native-event-listeners";
import {
  AllProgressCheckStatuses,
  CompetenceActivityClass,
  EvidenceAttachment,
  EvidencePart,
  FilledInBy,
  IAccessibilitySettings,
  IEvidence,
  IUser,
  LearningObjective,
  ProgramData,
  ProgressCheck,
  ProgressCheckStatus,
} from "@interfaces/Interfaces";
import {
  accessibilityAtom,
  appInfoAtom,
  deviceInfoAtom,
  evidenceAtom,
  progressCheckDataAtom,
  userAtom,
} from "@state/State";
import { useRecoilValue, useRecoilValueLoadable } from "recoil";
import _ from "lodash";
import * as FirebaseService from "@controllers/FirebaseService";
import { DeviceInfo } from "@capacitor/device";
import { AppInfo } from "@capacitor/app";
import * as ProgramUtils from "@utils/programUtils";
import * as EvidenceUtils from "@utils/evidenceUtils";
import { useCallback, useEffect, useState } from "react";
import * as FileUtils from "@utils/fileUtils";
import "../../theme/tailwind.css";
import { useProgramData } from "@hooks/data/useProgramData";
import { useTextToSpeech } from "@hooks/accessibility/useTextToSpeech";
import { useProgramModuleStatus } from "@hooks/programs/useProgramModuleStatus";
import { userRolesSelector } from "@state/UserState";

// Extracted components
import ReadEvidenceHeader from "@components/readEvidence/ReadEvidenceHeader";
import EvidenceContent from "@components/readEvidence/EvidenceContent";
import { useSendEvidenceReminder } from "@hooks/evidence/useSendEvidenceReminder";
import { useProgramCycles } from "@hooks/programs/useProgramCycles";
import { PROGRAM_IDS } from "@constants/programs";
import { autoLearningOutcomeLegacyKeys } from "@constants/evidencePartsLookup";

// State interfaces for better organization
interface ProgramState {
  program: ProgramData | null;
  competences: CompetenceActivityClass[];
  progressCheck: ProgressCheck | null;
  progressChecks: ProgressCheck[];
  progressCheckCompetence: CompetenceActivityClass | null;
  progressCheckCompetences: CompetenceActivityClass[];
  programLearningObjectives: LearningObjective[];
}

interface EvidenceState {
  evidence: IEvidence | null;
  evidenceJSON: any;
  competence: CompetenceActivityClass | null;
  evidenceParts: EvidencePart[];
  id: string;
}

interface UIState {
  viewingAttachment: boolean;
  viewingAttachmentFilename: string;
  canEdit: boolean;
}

const ReadEvidence: React.FC = () => {
  const params = useParams<{ programId: string; progressCheckId: string; skillId: string; evidenceId: string }>();

  const { pauseAndHold } = useProgramModuleStatus(params.programId);
  const { currentProgramCycle } = useProgramCycles(params.programId);

  const { programData } = useProgramData();
  const { handleTTS, playing, stopTTS } = useTextToSpeech("readContainer");

  const user = useRecoilValueLoadable<IUser | null>(userAtom);
  const allEvidence = useRecoilValueLoadable<IEvidence[] | null>(evidenceAtom);
  const allUserRoles = useRecoilValue(userRolesSelector);
  const deviceInfo = useRecoilValue<DeviceInfo | null>(deviceInfoAtom);
  const appInfo = useRecoilValue<AppInfo | null>(appInfoAtom);
  const accessibilitySettings = useRecoilValueLoadable<IAccessibilitySettings | null>(accessibilityAtom);
  const allProgressCheckData = useRecoilValueLoadable<AllProgressCheckStatuses[] | null>(progressCheckDataAtom);

  // Organized state management - Group related state together
  const [programState, setProgramState] = useState<ProgramState>({
    program: null,
    competences: [],
    progressCheck: null,
    progressChecks: [],
    progressCheckCompetence: null,
    progressCheckCompetences: [],
    programLearningObjectives: [],
  });

  const [evidenceState, setEvidenceState] = useState<EvidenceState>({
    evidence: null,
    evidenceJSON: null,
    competence: null,
    evidenceParts: [],
    id: "",
  });

  const [uiState, setUIState] = useState<UIState>({
    viewingAttachment: false,
    viewingAttachmentFilename: "",
    canEdit: false,
  });

  // Keep this as separate state since it's used independently
  const [progressCheckData, setProgressCheckData] = useState<ProgressCheckStatus | null>(null);

  // Helper functions for grouped state updates
  const updateProgramState = useCallback((updates: Partial<ProgramState>) => {
    setProgramState((prev) => ({ ...prev, ...updates }));
  }, []);

  const updateEvidenceState = useCallback((updates: Partial<EvidenceState>) => {
    setEvidenceState((prev) => ({ ...prev, ...updates }));
  }, []);

  const updateUIState = useCallback((updates: Partial<UIState>) => {
    setUIState((prev) => ({ ...prev, ...updates }));
  }, []);

  // Backward compatible setter functions (demonstrating the new pattern)
  const setProgram = useCallback(
    (program: ProgramData | null) => {
      updateProgramState({ program });
    },
    [updateProgramState]
  );

  const setEvidence = useCallback(
    (evidence: IEvidence | null) => {
      updateEvidenceState({ evidence });
    },
    [updateEvidenceState]
  );

  const setEvidenceJSON = useCallback(
    (evidenceJSON: any) => {
      updateEvidenceState({ evidenceJSON });
    },
    [updateEvidenceState]
  );

  const setCanEdit = useCallback(
    (canEdit: boolean) => {
      updateUIState({ canEdit });
    },
    [updateUIState]
  );

  const setViewingAttachment = useCallback(
    (viewing: boolean) => {
      updateUIState({ viewingAttachment: viewing });
    },
    [updateUIState]
  );

  const setViewingAttachmentFilename = useCallback(
    (filename: string) => {
      updateUIState({ viewingAttachmentFilename: filename });
    },
    [updateUIState]
  );

  // Additional setters for demonstration - in a full refactor, these would be replaced with updateXState calls
  const setCompetences = useCallback(
    (competences: CompetenceActivityClass[]) => {
      updateProgramState({ competences });
    },
    [updateProgramState]
  );

  const setProgressChecks = useCallback(
    (progressChecks: ProgressCheck[]) => {
      updateProgramState({ progressChecks });
    },
    [updateProgramState]
  );

  const setProgressCheck = useCallback(
    (progressCheck: ProgressCheck | null) => {
      updateProgramState({ progressCheck });
    },
    [updateProgramState]
  );

  const setProgressCheckCompetences = useCallback(
    (progressCheckCompetences: CompetenceActivityClass[]) => {
      updateProgramState({ progressCheckCompetences });
    },
    [updateProgramState]
  );

  const setProgressCheckCompetence = useCallback(
    (progressCheckCompetence: CompetenceActivityClass | null) => {
      updateProgramState({ progressCheckCompetence });
    },
    [updateProgramState]
  );

  const setProgramLearningObjectives = useCallback(
    (programLearningObjectives: LearningObjective[]) => {
      updateProgramState({ programLearningObjectives });
    },
    [updateProgramState]
  );

  const setCompetence = useCallback(
    (competence: CompetenceActivityClass | null) => {
      updateEvidenceState({ competence });
    },
    [updateEvidenceState]
  );

  const setEvidenceParts = useCallback(
    (evidenceParts: EvidencePart[]) => {
      updateEvidenceState({ evidenceParts });
    },
    [updateEvidenceState]
  );

  const setId = useCallback(
    (id: string) => {
      updateEvidenceState({ id });
    },
    [updateEvidenceState]
  );

  // Legacy getters for backward compatibility (to be gradually replaced)
  const program = programState.program;
  const competences = programState.competences;
  const progressCheck = programState.progressCheck;
  const progressChecks = programState.progressChecks;
  const progressCheckCompetence = programState.progressCheckCompetence;
  const progressCheckCompetences = programState.progressCheckCompetences;
  const programLearningObjectives = programState.programLearningObjectives;

  const evidence = evidenceState.evidence;
  const evidenceJSON = evidenceState.evidenceJSON;
  const competence = evidenceState.competence;
  const evidenceParts = evidenceState.evidenceParts;
  const id = evidenceState.id;

  const sendEvidenceReminder = useSendEvidenceReminder(program?.ID, id);

  const viewingAttachment = uiState.viewingAttachment;
  const viewingAttachmentFilename = uiState.viewingAttachmentFilename;
  const canEdit = uiState.canEdit;

  const programRoles = allUserRoles.find((role) => role.programID === programState?.program?.ID);
  let userRoles: string[] = [];

  if (programRoles) {
    userRoles = ProgramUtils.mapProgramRolesToRoleNames(programRoles.programRoles);
  }

  const showDebugInfo = false;

  const sendSupportEmail = useCallback((): void => {
    if (user.state === "hasValue" && user.contents) {
      const url = "mailto:apps@class.co.uk";
      const subject = "ParaFolio - Feedback";

      const body = DataController.supportEmailBody(user.contents.userData, deviceInfo, appInfo);

      window.open(`${url}?subject=${subject}&body=${body}`, "_blank");
    }
  }, [appInfo, deviceInfo, user]);

  useEffect(() => {
    return () => {
      stopTTS();
    };
  }, []);

  useEffect(() => {
    const handleClick = async (event: Event): Promise<void> => {
      const element = event.target as HTMLAnchorElement;

      if (element.href && element.href?.includes("contactSupport")) {
        event.preventDefault();
        await FirebaseService.logEvent("support_link_pressed", {
          link: "contact_support",
          type: "app_support",
        });

        sendSupportEmail();
      }
    };

    const handleAuxClick = async (event: MouseEvent): Promise<void> => {
      const element = event.target as HTMLAnchorElement;

      if (event.button === 1) {
        if (element.href?.includes("mailto:apps@class.co.uk")) {
          event.preventDefault();
          await FirebaseService.logEvent("support_link_pressed", {
            link: "contact_support",
            type: "app_info_feedback",
          });

          sendSupportEmail();
        }
      }
    };

    document.addEventListener("click", handleClick);
    document.addEventListener("auxclick", handleAuxClick);

    return () => {
      document.removeEventListener("click", handleClick);
      document.removeEventListener("auxclick", handleAuxClick);
    };
  }, [sendSupportEmail]);

  useEffect(() => {
    const checkEvidenceIsLocked = (): void => {
      const evidenceLocked = evidence?.evidenceJSON.programInfo.locked;

      const evidenceCycle = evidence?.evidenceJSON.programInfo.programCycle;

      const witnessEvidenceApproved = evidence?.evidenceJSON.programInfo.witnessSimulationApproval;

      if (evidenceLocked) {
        setCanEdit(false);
        return;
      }

      if (witnessEvidenceApproved) {
        setCanEdit(false);
        return;
      }

      if (evidenceCycle && currentProgramCycle && evidenceCycle !== currentProgramCycle) {
        setCanEdit(false);
        return;
      }

      const approvalState = evidence?.evidenceJSON.programInfo?.approvalState;
      if (approvalState === "Approved" || approvalState === "Rejected") {
        setCanEdit(false);
        return;
      }

      let competence: CompetenceActivityClass | undefined = competences.find(
        (item: CompetenceActivityClass) => item.ID === evidenceJSON.programInfo.progressCheckCompetenceID
      );

      if (!competence) {
        competence = progressCheckCompetences.find(
          (item: CompetenceActivityClass) => item.ID === evidenceJSON.programInfo.progressCheckCompetenceID
        );
      }

      if (pauseAndHold) {
        setCanEdit(false);
      } else if (program && ProgramUtils.isCSM(program.ID)) {
        const canEdit = (!competence?.NoEdit || (competence?.NoEdit && evidence?.draft)) && !evidenceJSON.OnHoldReset;
        setCanEdit(canEdit ?? false);
      } else {
        let canEdit: boolean = false;

        if (program && ProgramUtils.isNQPProgram(program.ID)) {
          canEdit =
            (!competence?.isWellbeingActivity &&
              (!competence?.NoEdit || (competence?.NoEdit && evidence?.draft)) &&
              competence?.FilledInBy !== FilledInBy.Mentor &&
              !evidenceJSON.OnHoldReset &&
              ProgramUtils.canAddOrEditProgressCheckEvidence(
                progressCheckData?.submissions,
                progressCheck ?? undefined,
                allProgressCheckData.contents,
                allEvidence.contents,
                program?.ID,
                progressChecks
              ) &&
              !ProgramUtils.isProgramCompleteNQP(
                program.ProgressChecks,
                allProgressCheckData.contents,
                allEvidence.contents,
                program.ID
              )) ??
            false;
        } else {
          canEdit =
            (!competence?.isWellbeingActivity &&
              (!competence?.NoEdit || (competence?.NoEdit && evidence?.draft)) &&
              competence?.FilledInBy !== FilledInBy.Mentor &&
              !evidenceJSON.OnHoldReset &&
              ProgramUtils.canAddOrEditProgressCheckEvidence(
                progressCheckData?.submissions,
                progressCheck ?? undefined,
                allProgressCheckData.contents,
                allEvidence.contents,
                program?.ID,
                progressChecks
              )) ??
            false;
        }

        setCanEdit(canEdit);
      }
    };

    if (
      program &&
      // ((competences && competences.length) || (progressCheckCompetences && progressCheckCompetences.length)) &&
      evidenceJSON &&
      evidence
    ) {
      checkEvidenceIsLocked();
    } else {
      updateUIState({ canEdit: true });
    }
  }, [
    progressCheckData,
    competences.length,
    evidence,
    pauseAndHold,
    program,
    progressCheckCompetences,
    evidenceJSON,
    updateUIState,
  ]);

  useEffect(() => {
    const getProgressCheckData = (): void => {
      const dataForProgram: AllProgressCheckStatuses = allProgressCheckData.contents?.find(
        (item: any): item is AllProgressCheckStatuses => item.programID === program?.ID
      );

      const dataForProgressCheck = dataForProgram?.pCs.find((item) => item.pCId === progressCheck?.ID);
      dataForProgressCheck && setProgressCheckData(dataForProgressCheck);
    };

    if (allProgressCheckData.state === "hasValue") {
      getProgressCheckData();
    }
  }, [allProgressCheckData, program, progressCheck]);

  useEffect(() => {
    const getEvidence = (): void => {
      const _evidence: IEvidence = allEvidence.contents.find((item: IEvidence) => item.id === params.evidenceId);

      if (_evidence) {
        setEvidence(_evidence);
        setId(_evidence.id);

        if (_evidence.evidenceJSON) {
          const _evidenceJSON = _evidence.evidenceJSON;

          const competenceName = _evidenceJSON.programInfo?.competence;
          const programName = _evidenceJSON.programInfo?.program;

          const _program = programData.find((item: any): item is ProgramData => item && item.Name === programName);
          const _competences: any = _program?.CompetenceActivity ?? [];

          const _competence = _competences?.find(
            (item: any): item is CompetenceActivityClass => item.Name === competenceName
          );

          if (_program) {
            if (ProgramUtils.shouldGetProgressCheckInfo(_program.ID)) {
              setProgram(_program);

              const progressCheckID = _evidenceJSON.programInfo?.progressCheckID;
              const progressCheckCompetenceID = _evidenceJSON.programInfo?.progressCheckCompetenceID;

              const _progressChecks: any = _program.ProgressChecks ?? [];

              if (_progressChecks) {
                setProgressChecks(_progressChecks);

                const _progressCheck = _progressChecks.find(
                  (item: any): item is ProgressCheck => item.ID === progressCheckID
                );
                const usesAdditionalEvidenceDefinitions = _program.UsesAdditionalEvidenceDefinitions;

                if (_progressCheck) {
                  setProgressCheck(_progressCheck);

                  const _progressCheckCompetences = _progressCheck["Competence/Activity"] || [];

                  if (_progressCheckCompetences) {
                    setProgressCheckCompetences(_progressCheckCompetences);

                    const _progressCheckCompetence = _progressCheckCompetences.find(
                      (item: any): item is CompetenceActivityClass => item.ID === progressCheckCompetenceID
                    );

                    if (_progressCheckCompetence) {
                      const evidenceDefinitions = _progressCheckCompetence?.["Evidence Definitions"][0];

                      const _evidenceParts = EvidenceUtils.getEvidenceParts(evidenceDefinitions);

                      setProgressCheckCompetence(_progressCheckCompetence);
                      setEvidenceParts(_evidenceParts);
                    }
                  }
                } else if (usesAdditionalEvidenceDefinitions) {
                  const _evidenceDefinitions = _program["Evidence Definitions"] || [];
                  const _formEvidenceDefinitions =
                    _program["WPBA Forms"]?.flatMap((form) => form["Evidence Definitions"] ?? []) ?? [];

                  if (_evidenceDefinitions && _evidenceDefinitions.length > 0) {
                    const evidenceType = ProgramUtils.getEvidenceType({
                      evidenceJSON: _evidence.evidenceJSON,
                      usesAdditionalEvidenceDefinitions: usesAdditionalEvidenceDefinitions ?? false,
                      evidenceDefinitions: _evidenceDefinitions,
                      formEvidenceDefinitions: _formEvidenceDefinitions,
                    });

                    let _evidenceDefinition: any;

                    if (evidenceType === "capabilities") {
                      _evidenceDefinition = _evidenceDefinitions.find(
                        (item) => item.EvidenceSelectionType === "capabilities"
                      );
                    } else if (evidenceType === "skills") {
                      _evidenceDefinition = _evidenceDefinitions.find(
                        (item) => item.EvidenceSelectionType === "skills"
                      );
                    } else if (evidenceType === "medical-imaging") {
                      _evidenceDefinition = _evidenceDefinitions.find(
                        (item) => item.EvidenceSelectionType === "medical-imaging"
                      );
                    } else if (evidenceType === "witness-simulation") {
                      _evidenceDefinition = _evidenceDefinitions.find(
                        (item) => item.EvidenceSelectionType === "witness-simulation"
                      );
                    } else if (evidenceType === "wpba-supervision-form") {
                      _evidenceDefinition = _formEvidenceDefinitions.find(
                        (item) => item.ID === _evidence.evidenceJSON.programInfo.programEvidenceDefinitionID
                      );
                    } else if (evidenceType === "ropp-workbook") {
                      _evidenceDefinition = _evidenceDefinitions.find(
                        (item) => item.EvidenceSelectionType === "ropp-workbook"
                      );
                    } else if (evidenceType === "cap-workbook") {
                      _evidenceDefinition = _evidenceDefinitions.find(
                        (item) => item.EvidenceSelectionType === "cap-workbook"
                      );
                    }

                    const _evidenceParts = _evidenceDefinition?.["Evidence parts"] ?? [];
                    const learningObjectives = ProgramUtils.getAllLearningObjectivesForRole(_program, userRoles[0]);

                    setEvidenceParts(_evidenceParts);
                    setProgramLearningObjectives(learningObjectives);
                  }
                }
              }
            } else {
              const _competences = _program.CompetenceActivity || [];
              const _evidenceDefinitions = _program["Evidence Definitions"] || [];

              setProgram(_program);

              if (_competences && _competences.length > 0) {
                setCompetences(_competences);

                if (_competence) {
                  const evidenceDefinitions = _competence["Evidence Definitions"][0];

                  const _evidenceParts = evidenceDefinitions?.["Evidence parts"] || [];

                  setEvidenceParts(_evidenceParts);
                  setCompetence(_competence);
                }
              }

              if (_evidenceDefinitions && _evidenceDefinitions.length === 1) {
                const _evidenceParts = _evidenceDefinitions[0]?.["Evidence parts"] || [];
                setEvidenceParts(_evidenceParts);
              } else if (_evidenceDefinitions && _evidenceDefinitions.length > 1) {
                const usesAdditionalEvidenceDefinitions = _program.UsesAdditionalEvidenceDefinitions;
                const _evidenceDefinitions = _program["Evidence Definitions"] || [];

                if (_evidenceDefinitions && _evidenceDefinitions.length > 0) {
                  const evidenceType = ProgramUtils.getEvidenceType({
                    evidenceJSON: _evidence.evidenceJSON,
                    usesAdditionalEvidenceDefinitions: usesAdditionalEvidenceDefinitions ?? false,
                    evidenceDefinitions: _evidenceDefinitions,
                  });

                  let _evidenceDefinition: any;

                  if (evidenceType === "capabilities") {
                    _evidenceDefinition = _evidenceDefinitions.find(
                      (item) => item.EvidenceSelectionType === "capabilities"
                    );
                  } else if (evidenceType === "skills") {
                    _evidenceDefinition = _evidenceDefinitions.find((item) => item.EvidenceSelectionType === "skills");
                  } else if (evidenceType === "medical-imaging") {
                    _evidenceDefinition = _evidenceDefinitions.find(
                      (item) => item.EvidenceSelectionType === "medical-imaging"
                    );
                  } else if (evidenceType === "witness-simulation") {
                    _evidenceDefinition = _evidenceDefinitions.find(
                      (item) => item.EvidenceSelectionType === "witness-simulation"
                    );
                  } else if (evidenceType === "ropp-workbook") {
                    _evidenceDefinition = _evidenceDefinitions.find(
                      (item) => item.EvidenceSelectionType === "ropp-workbook"
                    );
                  } else if (evidenceType === "cap-workbook") {
                    _evidenceDefinition = _evidenceDefinitions.find(
                      (item) => item.EvidenceSelectionType === "cap-workbook"
                    );
                  }

                  const _evidenceParts = _evidenceDefinition?.["Evidence parts"] ?? [];
                  const learningObjectives = ProgramUtils.getAllLearningObjectivesForRole(_program, userRoles[0]);

                  setEvidenceParts(_evidenceParts);
                  setProgramLearningObjectives(learningObjectives);
                }
              }
            }
          }

          setEvidenceJSON(_evidenceJSON);
        }
      }
    };

    if (allEvidence.state === "hasValue" && allEvidence.contents) {
      getEvidence();
    }
  }, [allEvidence, program?.version, progressCheckCompetences, setEvidence]);

  useEffect(() => {
    EventRegister.emit("tab-bar/visibility-changed", false);

    return () => {
      EventRegister.emit("tab-bar/visibility-changed", true);
    };
  }, []);

  const editEvidence = (): void => {
    const _evidence = _.pickBy(_.cloneDeep(evidence), _.identity);

    if (_evidence.evidenceJSON) {
      let data: object = {};

      const usesAdditionalEvidenceDefinitions = program?.UsesAdditionalEvidenceDefinitions;
      const evidenceDefinitions = program?.["Evidence Definitions"] || [];
      const formEvidenceDefinitions =
        program?.["WPBA Forms"]?.flatMap((form) => form["Evidence Definitions"] ?? []) ?? [];
      const mandatoryStandards = ProgramUtils.getMandatoryStandards(user.contents, program!);
      const optionalStandards = ProgramUtils.getOptionalStandards(user.contents, program!);
      const skills = ProgramUtils.getSkillsForRole(user.contents, program!);

      const evidenceType = ProgramUtils.getEvidenceType({
        evidenceJSON: _evidence.evidenceJSON,
        usesAdditionalEvidenceDefinitions: usesAdditionalEvidenceDefinitions ?? false,
        evidenceDefinitions,
        formEvidenceDefinitions,
      });

      if (evidenceType === "skills") {
        data = {
          ...data,
          program: program,
          ...(competences.length > 0 && { competences }),
          ...(evidenceDefinitions.length > 0 &&
            usesAdditionalEvidenceDefinitions && {
              evidenceDefinitions: evidenceDefinitions[0],
            }),
          competence: competence,
          evidence: _evidence,
          ...(progressCheckCompetence && {
            progressCompetence: progressCheckCompetence,
          }),
          progressCheck: progressCheck,
          ...(progressChecks.length > 0 && !usesAdditionalEvidenceDefinitions && { progressChecks }),
          ...(mandatoryStandards.length > 0 && { mandatoryStandards }),
          ...(optionalStandards.length > 0 && { optionalStandards }),
          ...(skills.length > 0 && { skills }),
          evidenceJSON: _evidence.evidenceJSON,
          skill: null,
          id: _evidence.id,
          learningOutcomes: progressCheckCompetence?.["Learning Objective"] ?? [],
          progressCheckCompetence: progressCheckCompetence,
          currentProgressCheckId: progressCheck?.ID ?? "",
          disableProgressCheckDropdown: progressCheck !== undefined,
          disableProgressCheckCompetenceDropdown: progressCheckCompetence !== undefined,
        };
      } else if (evidenceType === "witness-simulation") {
        const evidenceDefinition = evidenceDefinitions.find(
          (item) => item.EvidenceSelectionType === "witness-simulation"
        );
        const preselectedSkillID = _evidence.evidenceJSON.programInfo?.witnessSimulationSkillID;
        const preselectedSkill = skills.find((item) => item.ID === preselectedSkillID);

        data = {
          ...data,
          program: program,
          evidenceDefinitions: evidenceDefinition,
          evidence: _evidence,
          ...(skills.length > 0 && { skills }),
          ...(preselectedSkill && { preselectedSkill }),
          evidenceJSON: _evidence.evidenceJSON,
          id: _evidence.id,
        };
      } else if (evidenceType === "capabilities") {
        const evidenceDefinition = evidenceDefinitions.find((item) => item.EvidenceSelectionType === "capabilities");
        const learningObjectives = program ? ProgramUtils.getAllLearningObjectivesForRole(program, userRoles[0]) : [];

        data = {
          ...data,
          program: program,
          ...(evidenceDefinitions.length > 0 &&
            usesAdditionalEvidenceDefinitions && {
              evidenceDefinitions: evidenceDefinition,
            }),
          ...(mandatoryStandards.length > 0 && { mandatoryStandards }),
          ...(optionalStandards.length > 0 && { optionalStandards }),
          ...(learningObjectives.length > 0 && { learningObjectives }),
          evidenceJSON: _evidence.evidenceJSON,
          id: _evidence.id,
        };
      } else if (evidenceType === "medical-imaging") {
        const evidenceDefinition = evidenceDefinitions.find((item) => item.EvidenceSelectionType === "medical-imaging");
        const learningObjectives = program ? ProgramUtils.getAllLearningObjectivesForRole(program, userRoles[0]) : [];

        data = {
          program: program,
          ...(evidenceDefinitions.length > 0 &&
            usesAdditionalEvidenceDefinitions && {
              evidenceDefinitions: evidenceDefinition,
            }),
          ...(mandatoryStandards.length > 0 && { mandatoryStandards }),
          ...(optionalStandards.length > 0 && { optionalStandards }),
          ...(learningObjectives.length > 0 && { learningObjectives }),
          ...(skills.length > 0 && { skills }),
          evidenceJSON: _evidence.evidenceJSON,
          id: _evidence.id,
        };
      } else if (evidenceType === "wpba-supervision-form") {
        const evidenceDefinition = formEvidenceDefinitions.find(
          (item) => item.ID === _evidence.evidenceJSON.programInfo.programEvidenceDefinitionID
        );

        const wpbaForm = program?.["WPBA Forms"]?.find((form) =>
          form["Evidence Definitions"]?.find(
            (item) => item.ID === _evidence.evidenceJSON.programInfo.programEvidenceDefinitionID
          )
        );

        data = {
          program: program,
          ...(evidenceDefinitions.length > 0 &&
            usesAdditionalEvidenceDefinitions && {
              evidenceDefinitions: evidenceDefinition,
            }),
          ...(mandatoryStandards.length > 0 && { mandatoryStandards }),
          ...(optionalStandards.length > 0 && { optionalStandards }),
          ...(skills.length > 0 && { skills }),
          evidenceJSON: _evidence.evidenceJSON,
          id: _evidence.id,
          wpbaForm: wpbaForm,
        };
      } else if (evidenceType === "ropp-workbook") {
        const evidenceDefinition = evidenceDefinitions.find((item) => item.EvidenceSelectionType === "ropp-workbook");
        const learningObjectives = program ? ProgramUtils.getAllLearningObjectivesForRole(program, userRoles[0]) : [];

        data = {
          ...data,
          program: program,
          ...(evidenceDefinitions.length > 0 &&
            usesAdditionalEvidenceDefinitions && {
              evidenceDefinitions: evidenceDefinition,
            }),
          ...(mandatoryStandards.length > 0 && { mandatoryStandards }),
          ...(optionalStandards.length > 0 && { optionalStandards }),
          ...(learningObjectives.length > 0 && { learningObjectives }),
          evidenceJSON: _evidence.evidenceJSON,
          id: _evidence.id,
        };
      } else if (evidenceType === "cap-workbook") {
        const evidenceDefinition = evidenceDefinitions.find((item) => item.EvidenceSelectionType === "cap-workbook");
        const learningObjectives = program ? ProgramUtils.getAllLearningObjectivesForRole(program, userRoles[0]) : [];

        data = {
          ...data,
          program: program,
          ...(evidenceDefinitions.length > 0 &&
            usesAdditionalEvidenceDefinitions && {
              evidenceDefinitions: evidenceDefinition,
            }),
          ...(mandatoryStandards.length > 0 && { mandatoryStandards }),
          ...(optionalStandards.length > 0 && { optionalStandards }),
          ...(learningObjectives.length > 0 && { learningObjectives }),
          evidenceJSON: _evidence.evidenceJSON,
          id: _evidence.id,
        };
      } else {
        const _evidenceDefinition = usesAdditionalEvidenceDefinitions
          ? evidenceDefinitions.find((d) => d.ID === _evidence.evidenceJSON.programInfo?.programEvidenceDefinitionID)
          : evidenceDefinitions[0];

        const skillID = _evidence.evidenceJSON.programInfo?.skillID;
        const _autoFilledSkill = skillID ? skills.find((s) => s.ID === skillID) ?? null : null;
        const existingApprovalState = _evidence.evidenceJSON.programInfo?.approvalState;

        data = {
          program: program,
          ...(competences.length > 0 && { competences }),
          ...(_evidenceDefinition && { evidenceDefinitions: _evidenceDefinition }),
          competence: competence,
          evidence: _evidence,
          ...(progressCheckCompetence && {
            progressCompetence: progressCheckCompetence,
          }),
          progressCheck: progressCheck,
          ...(progressChecks.length > 0 && { progressChecks }),
          ...(mandatoryStandards.length > 0 && { mandatoryStandards }),
          ...(optionalStandards.length > 0 && { optionalStandards }),
          ...(skills.length > 0 && { skills }),
          evidenceJSON: _evidence.evidenceJSON,
          skill: null,
          id: _evidence.id,
          learningOutcomes: progressCheckCompetence?.["Learning Objective"] ?? [],
          progressCheckCompetence: progressCheckCompetence,
          currentProgressCheckId: progressCheck?.ID ?? "",
          disableProgressCheckDropdown: progressCheck !== undefined,
          disableProgressCheckCompetenceDropdown: progressCheckCompetence !== undefined,
          ...(_autoFilledSkill && { autoFilledSkill: _autoFilledSkill }),
          ...(existingApprovalState && { approvalState: existingApprovalState }),
        };
      }

      EventRegister.emit("evidence/add-program-evidence", data);
    } else {
      EventRegister.emit("evidence/evidence-card-clicked", _evidence);
    }
  };

  const viewAttachment = async (attachment: EvidenceAttachment): Promise<void> => {
    try {
      if (attachment.url && user.contents) {
        setViewingAttachment(true);
        setViewingAttachmentFilename(attachment.name);

        await FileUtils.viewAttachment(user.contents, id, attachment);

        setViewingAttachment(false);
        setViewingAttachmentFilename("");
      }
    } catch (error) {
      console.log(error);
      console.log("viewAttachment error", error);
      setViewingAttachment(false);
      setViewingAttachmentFilename("");
      window.alert("An error occurred when opening attachment");
    }
  };

  const getLearningOutcomesForEvidencePart = (): LearningObjective[] => {
    if (program?.ID === PROGRAM_IDS.SWAST_SP_AP_RP) {
      let userRoles: string[] = [];
      const programRoles = allUserRoles.find((role) => role.programID === program?.ID);

      if (programRoles) {
        userRoles = ProgramUtils.mapProgramRolesToRoleNames(programRoles.programRoles);
      }

      return ProgramUtils.getAllLearningObjectivesForRole(program, userRoles[0]);
    }

    if (progressCheckCompetence) {
      return progressCheckCompetence["Learning Objective"] ?? [];
    }

    if (programLearningObjectives.length > 0) {
      return programLearningObjectives;
    }

    return [];
  };

  const getLegacyEvidenceParts = (): string[] => {
    if (evidenceJSON) {
      const hasAutomaticLearningOutcomes =
        progressCheckCompetence?.["LOs Automatic"] || competence?.["LOs Automatic"] || false;

      const copy = structuredClone(evidenceJSON);
      delete copy.programInfo;

      const keys = Object.keys(copy);

      const hasNOSSelectPart = evidenceParts.some((part: EvidencePart) => EvidenceUtils.isNOSSelect(part));

      const allEvidenceParts = EvidenceUtils.flattenEvidenceParts(evidenceParts);

      const keysWithoutEvidencePart = keys
        .filter((key) => {
          const evidencePart = allEvidenceParts.find((part: EvidencePart) => part.Name === key);

          if (evidencePart) return false;

          if (key === "Title") return false;

          if ((key === "LOs" || key === "Comps") && hasNOSSelectPart) {
            return false;
          }

          if (hasAutomaticLearningOutcomes && autoLearningOutcomeLegacyKeys.has(key)) {
            return false;
          }

          return true;
        })
        .filter((key) => key !== "OnHoldReset");

      return keysWithoutEvidencePart;
    }

    return [];
  };

  const legacyParts = useMemo(() => getLegacyEvidenceParts(), [evidenceJSON, evidenceParts]);

  return (
    <IonPage>
      <ReadEvidenceHeader
        accessibilitySettings={accessibilitySettings.contents}
        canEdit={canEdit}
        playing={playing}
        onTTSClick={handleTTS}
        onEditClick={editEvidence}
      />
      <IonContent className="page-background whiteBackground">
        <ContentContainer
          tabBarDisabled
          id="readContainer"
        >
          <EvidenceContent
            evidence={evidence}
            evidenceJSON={evidenceJSON}
            evidenceParts={evidenceParts}
            legacyParts={legacyParts}
            user={user.contents!}
            program={program}
            viewingAttachment={viewingAttachment}
            viewingAttachmentFilename={viewingAttachmentFilename}
            onViewAttachment={viewAttachment}
            getLearningOutcomesForEvidencePart={getLearningOutcomesForEvidencePart}
            onResend={sendEvidenceReminder.mutateAsync}
            isCoolingDown={sendEvidenceReminder.isCoolingDown}
            remindedAt={sendEvidenceReminder.remindedAt}
          />

          {showDebugInfo && evidence && (
            <div className="readEvidenceBlock">
              <div className="readEvidenceHeader">{"Debug info - evidence"}</div>
              <div className="readEvidenceText">
                <pre>{JSON.stringify(evidence, null, 2)}</pre>
              </div>
              <div className="readEvidenceHeader mt-2">{"Debug info - evidenceJSON"}</div>
              <div className="readEvidenceText">
                <pre>{JSON.stringify(evidenceJSON, null, 2)}</pre>
              </div>
            </div>
          )}
        </ContentContainer>
      </IonContent>
    </IonPage>
  );
};

export default withRouter(ReadEvidence);
